import React, { useContext, useEffect } from 'react';
import Styled from 'styled-components';
import { useRouter } from 'next/router';
import { Trans } from '@lingui/react';
import Hero from '@ecosystems/landing_pages/hero/Hero';
import {
  TextWrapper,
  TextWrapperInner,
  Title,
  SubTitle,
  Paragraph,
  VideoWrapper,
  Disclaimer,
} from '@ecosystems/landing_pages/hero/components';
import { Container, Flex, Box } from '@components/layout/Grid';
import SectionHeader from '@components/SectionHeader';
import InstructorMeta from '@components/InstructorMeta';
import Icon, { icons } from '@components/Icon';
import SpaIcon from '@material-ui/icons/Spa';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import LanguageContext from '@lib/contexts/languageContext';
import messageStripe from '@components/MessageStripe';
import { TestimonialStatic } from '@components/texts';
import { ThreeColumnGrid } from '@components/CSSGrid';
import MetaTags from '@components/MetaTags';
import Carousel from '@components/Carousel';
import BlogItemCard, {
  Label,
  CardDescription,
} from '@organisms/cards/BlogItemCard';
import ThumbnailWithRatio, {
  BackgroundImage,
} from '@components/ThumbnailWithRatio';
import Button from '@components/buttons';
import programCard from '@organisms/cards/ProgramCard';
import CategoryIcon from '@assets/ChallengeTypeIc';
import CurvedArrow from '@assets/ArrowDownCurved';
import { UserContext } from '@lib/contexts/UserProvider';
import redirect from '@lib/redirect';
import { Video } from '@gql/generated';
import PricingSection from '@ecosystems/pricing/PricingSection';

const GoldButton = Styled(Button)`
  color: #45350C;
  margin-right: 10px;
`;

const GoldOutline = Styled(GoldButton)`
  border-color: #CCB375;
  padding-top: 11px;
  padding-bottom: 11px;
`;

const MessageStripe = Styled(messageStripe)`
  p {
    font-size: 14px;
    font-weight: 500;
    margin: 10px auto;
  }
`;

const HeroSection = Styled(Hero)`
  b {
    color: #253238;
    white-space: nowrap;
  }

  ${TextWrapper} {
    padding-right: 8%;
    margin: 0 auto;

    ${TextWrapperInner} {
      max-width: 720px;
    }
    
    ${Title} {
      display: flex;
      align-items: center;
      font-size: 34px;
      line-height: 1.2;
      margin-bottom: 0px;

      span {
        font-size: 11px;
        color: ${({ theme }) => theme.colors.neutrals.white};
        font-weight: 500;
        border-radius: 3px;
        padding: 5px;
        margin-left: 12px;
        background-color: #129EDC;
      }
    }

    ${SubTitle} {
      font-size: 20px;
      line-height: 1.4;
      margin-top: 24px;
      margin-bottom: 20px;
    }

    ${Disclaimer} {
      max-width: 400px;
      font-size: 13px;
      margin-top: 10px;
    }

    @media (max-width: ${props => props.theme.viewports.tablet}px) {
      ${Title} {
        font-size: 24px;
      }
      margin-top: 20px;
    }

    @media (max-width: ${props => props.theme.viewports.mobile}px) {
      padding-right: 0;
    }
  }

  ${VideoWrapper} {
    display: flex;
    align-items: center;
  }
`;

const StepByStepSection = Styled.div`
  background-color: #FAF7F1;
`;

const StepsTitle = Styled.p`
  font-size: 20px;
`;

const StepSection = Styled(Hero)`
  width: 100%;
  flex-direction: column;
  align-items: center;
  margin-bottom: 40px;

  &.StepSection--First {
    ${ThumbnailWithRatio.toString()} ${BackgroundImage}{
      background-size: auto 100%;
      background-repeat: no-repeat;
    }
  }
  
  ${TextWrapper}{
    width: 100%;
    
    ${TextWrapperInner}{
      max-width: 540px;
      align-items: center;
      text-align: center;
      margin: 0 auto;
    }
    
    ${Title} {
      font-size: 22px;
      margin-bottom: 20px;
    }

    ${SubTitle} {
      font-size: 16px;
      line-height: 24px;
    }
  }

  ${VideoWrapper} {
    max-width: 620px;
    width: 100%;
    border-radius: 30px;
    padding: 60px 0px;
    background-color: rgba(205, 180, 119, 0.4);

    ${ThumbnailWithRatio.toString()} ${BackgroundImage}{
      background-color: unset;
    }
  }
  

  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    flex-direction: row-reverse;
  
    &:nth-of-type(even){
      flex-direction: row;

      ${TextWrapper} {
        padding-left: 10%;
      }
    }

    ${TextWrapper} {
      width: 50%;

      ${TextWrapperInner}{
        align-items: initial;
        text-align: left;
      }
    }
    
    ${VideoWrapper} {
      width: 50%;
    }
  }
`;

const FeaturesSection = Styled(Container)`
  display: flex;
  flex-direction: column;
  padding: 60px 20px;
  background-color: #D9E8E7;

  .swiper-wrapper {
    max-width: 1280px;
    margin: 0 auto;
  }
`;

const FeatureCard = Styled(BlogItemCard)`
  width: 320px !important;
  height: auto;
  padding: 15px 20px 50px;
  margin-right: 20px;
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  cursor: unset;

  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    width: 380px !important;
  }
  
  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    width: 400px !important;
  }
  
  ${ThumbnailWithRatio.toString()}{
    display: flex;
    align-items: center;
    padding-top: 20px;

    ${BackgroundImage} {
      position: static;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      margin: 0 auto;
      padding: 20px;
      box-sizing: content-box;
      background-size: 100px;
      background-repeat: no-repeat;
      background-color: rgba(136, 191, 188, 0.3);
    }
  }

  ${Label.toString()}{
    display: block;
    font-size: 18px;
    font-weight: 700;
    text-align: center;
  } 

  ${CardDescription}{
    height: auto !important;
    font-size: 14px;
    text-align: center;
    -webkit-line-clamp: unset;
    -webkit-box-orient: unset;
  }

  .ReadMore--btn {
    display: none;
  }
`;

const ProgramsWrapper = Styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  grid-gap: 20px;
`;

const ProgramCategory = Styled.p`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100%;
  color: ${({ theme }) => theme.colors.neutrals.white};
  font-weight: 500;
  border-radius: 0 3px 0 0;
  margin: 0;
  background-color: rgba(204, 179, 117, .9);
`;

const ProgramCard = Styled(programCard)`
  /* for small screen < 340px */
  max-width: 90vw;
`;

const PerksWrapper = Styled.div`
  position: relative;
`;

const PerksHero = Styled(Hero)`
  width: 100%;
  max-width: 1240px;
  padding: 40px 20px;
  margin: 40px 0;
  background-color: ${({ theme }) => theme.colors.channels.meditation};;
  

  ${TextWrapper} {
    width: 100%;
    margin: 0 auto;

    ${Title} {
      font-size: 34px;
      color: ${({ theme }) => theme.colors.neutrals.white};
      font-weight: 500;
    }

    ${Paragraph} {
      font-size: 14px;
    }

    ${Disclaimer} {
      max-width: 400px;
      color: ${({ theme }) => theme.colors.neutrals.white};
      font-size: 13px;
      margin-top: 10px;
    }

    button {
      color: ${({ theme }) => theme.colors.channels.meditation};
    }
  }

  ${VideoWrapper} {
    display: none;
  }

  @media(min-width: 1024px){
    padding-left: 60px;
    padding-right: 60px;
    margin-left: 20px;
    margin-right: 20px;

    ${TextWrapper} {
      width: 50%;
    }

    ${VideoWrapper} {
      display: initial;
      align-self: center;
      width: 256px;
      height: 256px;
      margin: 0 auto;

      [data-bg] {
        background-color: unset;
        background-size: contain;
        background-repeat: no-repeat;
      }
    }
  }
`;

const List = Styled.ul`
  color: ${({ theme }) => theme.colors.neutrals.white};
  font-size: 16px;
  line-height: 1.4;
  padding-left: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    
    i:before {
      font-size: 18px;
    }
  }
`;

const testimonials = {
  title: 'Vad säger våra medlemmar?',
  testimonials: [
    {
      name: 'Sharon',
      shortDescription: '26  år från Göteborg',
      testimonial:
        '“Jag har använt Yogobe i ett par månader nu och älskar hur varierad min yogapraktik och träning blivit!”',
      thumbnail: '/static/testimonials/image1.jpg',
      squareImage: '/static/testimonials/image1.jpg',
    },
    {
      name: 'Susanne von Hebel',
      shortDescription: '46 år från Hisings backa',
      testimonial:
        '“Yogobe har verkligen varit min räddning det här senaste året. Jag älskar att träna hemifrån! Det finns alltid någon ny klass att upptäcka, eller någon gammal favorit att återvända till.”',
      thumbnail: '/static/testimonials/image2.jpg',
      squareImage: '/static/testimonials/image2.jpg',
    },
    {
      name: 'Josefin',
      shortDescription: '33 år från Kungsbacka',
      testimonial:
        '“Det var inte förrän under min graviditet som jag hittade Yogobe. Nu kör jag ett par pass i veckan. Varvar klasser från “Mammamage” med Yoga Nidra.”',
      thumbnail: '/static/testimonials/image3.jpg',
      squareImage: '/static/testimonials/image3.jpg',
    },
  ],
};

const hereBannerList = [
  {
    icon: 'ygb-icon-playlist',
    text: <Trans id="landing_page.offer.blue_section.list_item1" />,
  },
  {
    icon: <CloudDownloadIcon />,
    text: <Trans id="landing_page.offer.blue_section.list_item2" />,
  },
  {
    icon: <SpaIcon />,
    text: <Trans id="landing_page.offer.blue_section.list_item3" />,
  },
  {
    icon: <PlaylistAddIcon />,
    text: <Trans id="landing_page.offer.blue_section.list_item4" />,
  },
];

const Tag = Styled.span<{ color?: string; bg?: string }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  line-height: 12px;
  text-transform: uppercase;
  margin-right: 5px;
  padding: 5px;
  border-radius: 3px;
  ${({ bg = '#CDB477', color = '#45350C' }) =>
    `color:${color}; background-color: ${bg};`};

  i {
    margin-right: 3px;
  }
`;

const CHALLENGE_PROGRAMS = [
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: '21-dagar-vardagsstark',
      path: 'programs/21-dagar-vardagsstark/overview',
      title: 'Vardagsstark 21 dagar',
      thumbnailUrl:
        'https://yogobe-assets-shrine-production.s3.eu-north-1.amazonaws.com/yogobe-production/uploads/store/image/e00004e0-d69c-46eb-a266-b26d085849d1/image/cbcf39e417dd5952b8952b03f57c3f0b.jpg',
      description:
        'En tre veckor lång utmaning med inspiration och vägledning till mer rörelse och återhämtning i vardagen!',
      category: 'Hållbarhet',
    },
  },
  // {
  //   tags: [
  //     <Tag key={0}>
  //       <Trans id="challenge_2021.metadata.tags.challenge" />
  //     </Tag>,
  //   ],
  //   program: {
  //     slug: 'mindfulness-kalender',
  //     path: 'programs/mindfulness-kalender/overview',
  //     title: 'Mindfulnesskalendern 2022',
  //     thumbnailUrl:
  //       'https://yogobe-assets-shrine-production.s3.eu-north-1.amazonaws.com/yogobe-production/uploads/store/image/d7dbd0b9-cc40-427c-a1d4-2f20d1a4ce2e/image/d27aa12f805f36626e3fcb5d45b5bda2.png',
  //     description:
  //       'En julkalender för dig som vill varva ner inför julen! Avsluta året med ett inre lugn och kliv in i det nya med närvaro, fokus och glädje.',
  //     category: 'Julkalender',
  //   },
  // },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: 'vakna-yogobe',
      title: 'Vakna med Yogobe',
      path: 'programs/vakna-yogobe/overview',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/images/images/d76/3a2/9d-/original/vakna_med_yogobe.jpeg?1656833846',
      description:
        'En utmaning som pågår under fyra veckor, med tre typer av sköna morgonklasser varje vecka!',
      category: 'Mjukstarta',
    },
  },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: '16-weeks-of-well',
      path: 'programs/16-weeks-of-well/overview',
      title: '16 veckor med Yogobe',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/images/images/81f/388/a9-/original/16_Weeks_of_Well_ha%CC%88lsa_yoga_tra%CC%88ning_rutiner_Yogobe.jpg?1649325583',
      description:
        'Ett 16 veckors utmaning för dig som vill få inspiration och stöd för ett ökat välmående, på alla plan.',
      category: 'Välmående',
    },
  },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: '7-day-kickstart',
      path: 'programs/7-day-kickstart/overview',
      title: '7 day challenge',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/images/images/1f7/0d3/15-/original/7daychallenge_program_page.jpg?1644405581',
      description:
        'För dig som vill komma igång med Yogobe och hitta glädjen i rutinen.',
      category: 'Kickstarta',
    },
  },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: 'beginner-baby',
      path: 'programs/beginner-baby/overview',
      title: 'Beginner baby',
      description:
        'För dig som vill komma igång med enkel träning & känna träningsglädje.',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/media_objects/images/93d/26d/7c-/original/Beginner-baby.jpg?1613037445',

      category: 'Nybörjare',
    },
  },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: 'strong-and-graceful',
      path: 'programs/strong-and-graceful/overview',
      title: 'Strong & Graceful',
      description: 'För dig som vill utmanas i såväl yoga som hållbar träning.',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/media_objects/images/de0/566/ff-/original/Strong-graceful.jpg?1613037499',

      category: 'Rörlighet',
    },
  },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: 'restore-and-relax',
      path: 'programs/restore-and-relax/overview',
      title: 'Restore & Relax',
      description:
        'Ett program för dig som upplever stress, smärta eller utmattning.',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/media_objects/images/77d/220/ad-/original/Relax-restore.jpg?1613037483',
      category: 'Relax',
    },
  },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: 'holy-trinity',
      path: 'programs/holy-trinity/overview',
      title: 'Holy Trinity',
      description:
        'För dig som vill utveckla din asana, fördjupa dig inom filosofi & spiritualism.',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/media_objects/images/e81/f6d/cd-/original/Holy-trinity.jpg?1613037466',
      category: 'Avancerat',
    },
  },
  {
    tags: [
      <Tag key={0}>
        <Trans id="challenge_2021.metadata.tags.challenge" />
      </Tag>,
    ],
    program: {
      slug: 'athlete-junkie',
      path: 'programs/athlete-junkie/overview',
      title: 'Athlete Junkie',
      description:
        'För dig som vill få en fysisk utmaning som ger märkbara resultat.',
      thumbnailUrl:
        'https://s3.eu-north-1.amazonaws.com/productionyogobestaticassets20200305182457522400000001/media_objects/images/c01/75a/53-/original/Athlete-junkies.jpg?1613037397',
      category: 'Svettas',
    },
  },
];

const FEATURES = [
  {
    thumbnailUrl: '/static/icons/mobility.svg',
    title: 'Kom igång',
    description:
      'Behöver du motivation för att komma igång med träningen , kanske efter en paus utan? Vår challenge "Beginner, baby" erbjuder enkel och fysisk träning, varvat med nybörjaryoga, pilates och enkel mindfulness.',
  },
  {
    thumbnailUrl: '/static/icons/stress.svg',

    title: 'Hantera stress',
    description:
      'Genom att fylla på med återhämtande yinyoga, introspektiv meditation och energigivande restorative flow ges du verktygen att hantera kropp och själ efter en längre period av stress och oro. Utforska vårt program "Relax & Restore".',
  },
  {
    thumbnailUrl: '/static/icons/athletes.svg',
    title: 'Varierad träning',
    description:
      'Träning och rörelse är en naturlig del av din vardag, men du behöver fylla på med en variation för att stärka din grund - därför passar utmaningen "Strong & graceful" dig alldeles utmärkt. I denna får du en mix av styrka, mobilitetsträning, dynamisk yoga och prehab.',
  },
];

export const CHALLENGE_CODE = 'challenge_2021';
const YogobeChallenge2021 = () => {
  const router = useRouter();
  const locale = useContext(LanguageContext);
  const [currentUser, { loading }] = useContext(UserContext);

  useEffect(() => {
    if (locale !== 'se') {
      router.replace('/[lang]/challenge', '/se/challenge');
    }
  }, []);

  const handleJoinClick = params => {
    if (currentUser?.id) {
      router.push({
        pathname: `/${locale}/subscription/select`,
        query: { ...params, to: router.asPath, campaign: 'Challenge' },
      });
    } else {
      router.push({
        pathname: `/${locale}/register/account`,
        query: {
          ...params,
          to: encodeURI(`/${locale}/challenge`),
          campaign: 'Challenge',
        },
      });
    }
  };

  if (loading || currentUser === undefined) {
    return null;
  }

  return (
    <>
      <MetaTags
        title="YOGOBE | Challenge"
        description="Investera i din hälsa. Bara 4 veckor. Välj 1 av 5 utmaningar - 100% anpassade för att möta dina behov, vare sig du är nybörjare, erfaren atlet, löpare eller yogaentusiast. Testa 14 dagar gratis, besök yogobe.com/challenge."
        thumbnailUrl="/static/Stocksy_txp16b150167qy200_Small_3367386.jpg"
      />

      <div>
        {/* without the wrapper div, html layout breaks because of difference between SSR & client trees */}
        <MessageStripe
          bg="#CCB375"
          color="#45350C"
          closable={false}
          show={!currentUser?.id}
        >
          <Trans id="challenge_2021.banner.discount_text" />
        </MessageStripe>
      </div>
      <div style={{ backgroundColor: '#FAF7F1' }}>
        <Container flexDirection={'column'} px={[25, 20, 20]} py={40}>
          <HeroSection
            title={
              <Trans
                id="challenge_2021.listing_page.hero.title"
                components={[<span key="0"></span>]}
              />
            }
            subtitle={
              currentUser?.id ? (
                <Trans id="challenge_2021.listing_page.hero.premium_desc" />
              ) : (
                <Trans id="challenge_2021.listing_page.hero.no_premium_desc" />
              )
            }
            actions={[
              ...(currentUser?.id
                ? [
                    <GoldButton
                      key={0}
                      appearance="default-gold"
                      href="#challenges"
                    >
                      <Trans id="challenge_2021.metadata.explore_challenges_btn" />
                    </GoldButton>,
                  ]
                : [
                    <GoldButton
                      key={0}
                      appearance="default-gold"
                      onClick={() => handleJoinClick({ code: CHALLENGE_CODE })}
                    >
                      <Trans id="challenge_2021.metadata.get_started_btn" />
                    </GoldButton>,
                  ]),
              <GoldOutline key={1} href="#more-info" appearance="outline">
                <Trans id="challenge_2021.metadata.how_it_works_btn" />
              </GoldOutline>,
            ]}
            image="/static/challenge_social.jpg"
            disclaimer={
              currentUser?.id ? (
                <Trans id="challenge_2021.listing_page.hero.premium_disclaimer" />
              ) : (
                <Trans id="challenge_2021.listing_page.hero.no_premium_disclaimer" />
              )
            }
            reverseColumns
          />
        </Container>
      </div>
      <Container id="challenges" my={40} px={20}>
        <Container flexDirection="column">
          <Box mb="30px">
            <SectionHeader
              title={<Trans id="challenge_2021.challenges_section.title" />}
            />
          </Box>
          <ProgramsWrapper>
            {CHALLENGE_PROGRAMS.map((item, i) => {
              const tags = [...item.tags];
              const hasAccess = currentUser?.programAccess?.find?.(
                access => access.program.slug === item.program.slug
              );

              if (hasAccess) {
                tags.push(
                  <Tag key={1} bg="#32847E" color="#fff">
                    <Icon name="ygb-icon-checkmark" color="inherit" />
                    <Trans id="challenge_2021.metadata.tags.participating" />
                  </Tag>
                );
              }

              return (
                <ProgramCard
                  key={i}
                  {...item}
                  program={{
                    ...item.program,
                    path: `${process.env.DOMAIN}/${locale}/${item.program.path}?challenge`,
                  }}
                  tags={tags}
                  customRightSection={
                    <ProgramCategory>
                      <CategoryIcon />
                      {item.program.category}
                    </ProgramCategory>
                  }
                />
              );
            })}
          </ProgramsWrapper>
        </Container>
      </Container>
      <FeaturesSection fullWidth>
        <Container flexDirection="column">
          <Box mb="30px">
            <SectionHeader
              title={<Trans id="challenge_2021.features_section.title" />}
            />
          </Box>
          <Carousel itemPerView={3}>
            {FEATURES.map((feature, i) => (
              <FeatureCard key={i} noTrancate {...feature} />
            ))}
          </Carousel>
        </Container>
        {!currentUser?.id ? (
          <Flex justifyContent="center" alignItems="center" mt={30}>
            <Button
              appearance="default-gold"
              onClick={() => handleJoinClick({ code: CHALLENGE_CODE })}
            >
              <Trans id="challenge_2021.metadata.start_now_btn" />
            </Button>
          </Flex>
        ) : (
          <Flex justifyContent="center" alignItems="center" mt={30}>
            <Button appearance="default-gold" href="#challenges">
              <Trans id="challenge_2021.metadata.explore_challenges_btn" />
            </Button>
          </Flex>
        )}
      </FeaturesSection>
      <StepByStepSection id="more-info">
        <Container flexDirection="column" px={[15, 20, 20]} pt={60}>
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            mb={40}
          >
            <StepsTitle>
              <Trans id="challenge_2021.steps_section.title" />
            </StepsTitle>
            <CurvedArrow />
          </Flex>
          <StepSection
            className="StepSection--First"
            image="/static/challenge-step1.png"
            title={<Trans id="challenge_2021.steps_section.step1.title" />}
            subtitle={<Trans id="challenge_2021.steps_section.step1.desc" />}
            actions={[
              ...(!currentUser?.id
                ? [
                    <Button
                      key={0}
                      appearance="default-purple"
                      onClick={() => handleJoinClick({ code: CHALLENGE_CODE })}
                    >
                      <Trans id="challenge_2021.metadata.get_started_btn" />
                    </Button>,
                  ]
                : []),
              <GoldOutline key={1} href="#challenges" appearance="outline">
                <Trans id="challenge_2021.metadata.browse_challenges" />
              </GoldOutline>,
            ]}
            reverseColumns
          />
          <StepSection
            image="/static/challenge-step2.png"
            title={<Trans id="challenge_2021.steps_section.step2.title" />}
            subtitle={<Trans id="challenge_2021.steps_section.step2.desc" />}
            disclaimer={
              <Trans id="challenge_2021.listing_page.hero.premium_disclaimer" />
            }
          />
          <StepSection
            image="/static/challenge-step3.png"
            title={<Trans id="challenge_2021.steps_section.step3.title" />}
            subtitle={<Trans id="challenge_2021.steps_section.step3.desc" />}
            actions={
              !currentUser?.id
                ? [
                    <Button
                      key={0}
                      appearance="default-purple"
                      onClick={() => handleJoinClick({ code: CHALLENGE_CODE })}
                    >
                      <Trans id="challenge_2021.metadata.get_started_btn" />
                    </Button>,
                  ]
                : []
            }
            reverseColumns
          />
          <StepSection
            image="/static/challenge-step4.png"
            title={<Trans id="challenge_2021.steps_section.step4.title" />}
            subtitle={<Trans id="challenge_2021.steps_section.step4.desc" />}
          />
        </Container>
      </StepByStepSection>
      <Container flexDirection="column" my={[25, 40, 40]} px={[15, 20, 20]}>
        <Box mb="50px">
          <SectionHeader title={testimonials.title} />
        </Box>
        <ThreeColumnGrid>
          {testimonials.testimonials.map(testimonial => (
            <Box key={testimonial.name} mb={40}>
              <InstructorMeta
                isClickable={false}
                instructor={{
                  thumbnailUrl: testimonial.thumbnail,
                  name: testimonial.name,
                  shortDescription: testimonial.shortDescription,
                }}
              />
              <TestimonialStatic>{testimonial.testimonial}</TestimonialStatic>
            </Box>
          ))}
        </ThreeColumnGrid>
      </Container>
      <PerksWrapper>
        <Container>
          <PerksHero
            title={<Trans id="landing_page.offer.blue_section.title" />}
            image="/static/onboarding-step2.png"
            description={
              <>
                <List>
                  {hereBannerList.map((item, i) => (
                    <li key={i}>
                      {typeof item.icon === 'string' ? (
                        <Icon
                          fontSize="14px"
                          name={item.icon as typeof icons[number]}
                        />
                      ) : (
                        item.icon
                      )}
                      <span style={{ marginLeft: '10px' }}>{item.text}</span>
                    </li>
                  ))}
                </List>
              </>
            }
            {...(!currentUser?.id
              ? {
                  actions: [
                    {
                      name: (
                        <Trans id="challenge_2021.metadata.start_now_btn" />
                      ),
                      theme: 'default-white',
                      onClick: () => handleJoinClick({ code: CHALLENGE_CODE }),
                    },
                  ],
                  disclaimer: (
                    <Trans id="challenge_2021.listing_page.hero.no_premium_disclaimer" />
                  ),
                }
              : {})}
            reverseColumns
          />
        </Container>
      </PerksWrapper>
      <PricingSection promotionCode="challenge_2021" />
    </>
  );
};

export const getServerSideProps = ctx => {
  const { query } = ctx;
  if (query.lang !== 'se') {
    redirect(ctx, `/se/challenge`);
  }

  return {
    props: {},
  };
};

export default YogobeChallenge2021;
